import { useState } from 'react'
import { CATALOGO_VIDROS, type FerramentaVidro, type IdVidro, type ProtecoesManejo } from '../../../dominio/didatico/manejoVidros'
import {
  PECAS_INTERVENCAO_TABLETOP,
  acoesDaPeca,
  situacaoEstabilizacao,
  type RegistroOperacao,
} from '../../../dominio/tabletop/atendimento'
import type { CenaTabletop, EstadoCortePecaTabletop, ObjetoTabletop, TipoObjetoTabletop } from '../../../dominio/tabletop'
import { EQUIPAMENTOS_ESTABILIZACAO, ROTULOS_PROTECAO } from '../useOperacoesSessao'

type Aba = 'estabilizar' | 'vidros' | 'cortes'

const IDS_VIDRO = Object.keys(CATALOGO_VIDROS) as IdVidro[]
const FERRAMENTAS: ReadonlyArray<{ id: FerramentaVidro; rotulo: string }> = [
  { id: 'puncaoAutomatica', rotulo: 'Punção automática' },
  { id: 'glasMaster', rotulo: 'Serra corta-vidro' },
]
const PROTECOES = Object.keys(ROTULOS_PROTECAO) as Array<keyof ProtecoesManejo>

const ROTULO_ESTADO_PECA: Record<string, string> = { aberta: 'aberta', afastada: 'afastada', removida: 'retirada' }
const ROTULO_ESTADO_VIDRO: Record<string, string> = {
  intacto: 'intacto',
  quebrado: 'quebrado',
  cortado: 'cortado',
  removido: 'removido',
  craqueladoComPelicula: 'craquelado (película)',
}

interface Props {
  readonly objeto: ObjetoTabletop
  readonly cena: CenaTabletop
  readonly operacoes: readonly RegistroOperacao[]
  /** alertas são avaliação do instrutor — não aparecem no modo aluno */
  readonly modoInstrutor: boolean
  /** Permite reutilizar as mesmas operações nas abas independentes do Tabletop. */
  readonly abasDisponiveis?: readonly Aba[]
  readonly onPosicionarEquipamento: (tipo: TipoObjetoTabletop) => void
  readonly onTestarEstabilidade?: () => void
  readonly onCortar: (idPeca: string, estado: EstadoCortePecaTabletop) => void
  readonly onVidro: (idVidro: IdVidro, ferramenta: FerramentaVidro) => void
  readonly onProtecao: (chave: keyof ProtecoesManejo) => void
}

/**
 * Cartão do veículo durante o atendimento: as ações das oficinas de
 * estabilização, vidros e corte, com poucos toques e sem digitar. Tudo age
 * na cópia da sessão e fica registrado; nada é bloqueado por erro pedagógico.
 */
export function OperacoesVeiculo(p: Props) {
  const [aba, setAba] = useState<Aba>('estabilizar')
  const [idVidro, setIdVidro] = useState<IdVidro>('parabrisa')
  const [idPeca, setIdPeca] = useState<string>('portaDianteiraEsquerda')
  const veiculo = p.objeto.veiculo
  if (!veiculo) return null

  const abas = p.abasDisponiveis ?? (['estabilizar', 'vidros', 'cortes'] as const)
  const abaAtual = abas.includes(aba) ? aba : abas[0]
  const estabilizacao = situacaoEstabilizacao(p.cena, p.objeto.id)
  const ultima = [...p.operacoes].reverse().find((op) => op.idVeiculo === p.objeto.id)

  return (
    <div className="atd-operacoes">
      <div className="atd-cartao-acao__linha">
        <strong>{p.objeto.nome}</strong>
        <span className={`atd-risco__situacao ${estabilizacao.primaria ? 'atd-operacoes--ok' : 'atd-operacoes--pendente'}`}>
          {estabilizacao.primaria ? 'Estabilizado' : 'Não estabilizado'}
        </span>
      </div>

      {abas.length > 1 && <div className="atd-operacoes__abas" role="tablist">
        {abas.map((id) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={abaAtual === id}
            className={`atd-chip-regiao ${abaAtual === id ? 'atd-chip-regiao--ativa' : ''}`}
            onClick={() => setAba(id)}
          >
            {{ estabilizar: 'Estabilizar', vidros: 'Vidros', cortes: 'Cortes' }[id]}
          </button>
        ))}
      </div>}

      {abaAtual === 'estabilizar' && (
        <div className="atd-operacoes__conteudo">
          {p.modoInstrutor && <span className="atd-cartao-acao__detalhe">{estabilizacao.mensagem}</span>}
          <span className="atd-form-intervencao__subtitulo">Posicionar (toque no chão ao lado do carro):</span>
          <div className="atd-risco__acoes">
            {EQUIPAMENTOS_ESTABILIZACAO.map((e) => (
              <button
                key={e.tipo}
                type="button"
                className="btn-acao-cad btn-acao-cad--secundario"
                onClick={() => p.onPosicionarEquipamento(e.tipo)}
              >
                {e.rotulo}
              </button>
            ))}
            {p.onTestarEstabilidade && (
              <button type="button" className="btn-acao-cad btn-acao-cad--secundario" onClick={p.onTestarEstabilidade}>
                Testar estabilidade
              </button>
            )}
          </div>
        </div>
      )}

      {abaAtual === 'vidros' && (
        <div className="atd-operacoes__conteudo">
          <div className="atd-form-intervencao__regioes" role="group" aria-label="Vidro">
            {IDS_VIDRO.map((id) => (
              <button
                key={id}
                type="button"
                className={`atd-chip-regiao ${idVidro === id ? 'atd-chip-regiao--ativa' : ''}`}
                onClick={() => setIdVidro(id)}
              >
                {CATALOGO_VIDROS[id].nome} · {ROTULO_ESTADO_VIDRO[veiculo.vidros[id] ?? 'intacto']}
              </button>
            ))}
          </div>
          <div className="atd-risco__acoes">
            {FERRAMENTAS.map((f) => (
              <button key={f.id} type="button" className="btn-acao-cad btn-acao-cad--secundario" onClick={() => p.onVidro(idVidro, f.id)}>
                {f.rotulo}
              </button>
            ))}
          </div>
          <span className="atd-form-intervencao__subtitulo">Proteções:</span>
          <div className="atd-risco__acoes">
            {PROTECOES.map((chave) => (
              <button
                key={chave}
                type="button"
                aria-pressed={veiculo.protecoes[chave]}
                className={`btn-acao-cad ${veiculo.protecoes[chave] ? 'btn-acao-cad--destaque' : 'btn-acao-cad--secundario'}`}
                onClick={() => p.onProtecao(chave)}
              >
                {ROTULOS_PROTECAO[chave]}
              </button>
            ))}
          </div>
        </div>
      )}

      {abaAtual === 'cortes' && (
        <div className="atd-operacoes__conteudo">
          <div className="atd-form-intervencao__regioes" role="group" aria-label="Peça">
            {PECAS_INTERVENCAO_TABLETOP.map((peca) => {
              const estado = veiculo.estadosPecas[peca.id]
              return (
                <button
                  key={peca.id}
                  type="button"
                  className={`atd-chip-regiao ${idPeca === peca.id ? 'atd-chip-regiao--ativa' : ''}`}
                  onClick={() => setIdPeca(peca.id)}
                >
                  {peca.nome}
                  {estado && ROTULO_ESTADO_PECA[estado] ? ` · ${ROTULO_ESTADO_PECA[estado]}` : ''}
                </button>
              )
            })}
          </div>
          <div className="atd-risco__acoes">
            {acoesDaPeca(idPeca).map((acao) => (
              <button
                key={acao.estado}
                type="button"
                className="btn-acao-cad btn-acao-cad--secundario"
                onClick={() => p.onCortar(idPeca, acao.estado)}
              >
                {acao.rotulo}
              </button>
            ))}
          </div>
        </div>
      )}

      {p.modoInstrutor && ultima && ultima.alertas.length > 0 && (
        <div className="atd-cartao-acao__consulta">
          <span className="atd-cartao-acao__detalhe">Alerta da última ação ({ultima.descricao}):</span>
          {ultima.alertas.map((alerta) => (
            <span key={alerta} className="atd-cartao-acao__valor">
              {alerta}
            </span>
          ))}
        </div>
      )}
    </div>
  )
}
